import { BuffType, Rarity, type BuffChargeState, type EquipmentItem, type HeroState } from '@/game/types';
import { parseHeroActivity } from './hero-activity-mapper';
import { parseActiveCrierSpeech } from '@/modules/town-news/utils/crier-announcement';
import type { HeroResponse } from '@/network/api';
import {
  debuffDurationsFromCatalog,
  mapHeroBuffsFromServer,
  mapHeroDebuffsFromServer,
} from '@/network/buffMap';

export function xpToNextLevel(level: number): number {
  if (level < 1) level = 1;
  if (level <= 9) return Math.round(180 * Math.pow(1.28, level - 1));
  if (level <= 29) return Math.round(1450 * Math.pow(1.15, level - 10));
  return Math.round(23000 * Math.pow(1.10, level - 30));
}

export function normalizeHeroModelVariant(v: unknown): number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 3 ? v : 0;
}

export function mapBuffCharges(
  raw: HeroResponse['buffCharges'],
): Partial<Record<BuffType, BuffChargeState>> {
  if (!raw) return {};
  const out: Partial<Record<BuffType, BuffChargeState>> = {};
  const validTypes = new Set(Object.values(BuffType) as string[]);
  for (const [key, val] of Object.entries(raw)) {
    if (validTypes.has(key)) {
      const charge: BuffChargeState = { remaining: val.remaining, periodEnd: val.periodEnd };
      if (typeof val.max === 'number' && Number.isSafeInteger(val.max) && val.max >= 0) {
        charge.max = val.max;
      }
      out[key as BuffType] = charge;
    }
  }
  return out;
}

export function mapEquipment(
  raw: HeroResponse['equipment'],
  res: HeroResponse,
): Record<string, EquipmentItem> {
  const out: Record<string, EquipmentItem> = {};
  const merged = raw ?? res.gear;
  const rawSlots = merged != null && typeof merged === 'object' ? merged : undefined;

  if (rawSlots) {
    for (const [slot, item] of Object.entries(rawSlots)) {
      out[slot] = {
        id: item.id,
        slot: item.slot ?? slot,
        formId: item.formId ?? '',
        name: item.name,
        rarity: (item.rarity?.toLowerCase() ?? 'common') as EquipmentItem['rarity'],
        ilvl: item.ilvl ?? 1,
        primaryStat: item.primaryStat ?? 0,
        statType: item.statType ?? 'mixed',
        subtype: item.subtype,
      };
    }
  }

  if (!out['main_hand'] && res.weapon) {
    out['main_hand'] = {
      id: res.weapon.id ?? 0,
      slot: 'main_hand',
      formId: '',
      name: res.weapon.name ?? 'Unknown',
      rarity: (res.weapon.rarity?.toLowerCase() ?? 'common') as EquipmentItem['rarity'],
      ilvl: res.weapon.ilvl ?? 1,
      primaryStat: res.weapon.damage ?? 0,
      statType: 'attack',
    };
  }

  if (!out['chest'] && res.armor) {
    out['chest'] = {
      id: res.armor.id ?? 0,
      slot: 'chest',
      formId: '',
      name: res.armor.name ?? 'Unknown',
      rarity: (res.armor.rarity?.toLowerCase() ?? 'common') as EquipmentItem['rarity'],
      ilvl: res.armor.ilvl ?? 1,
      primaryStat: res.armor.defense ?? 0,
      statType: 'defense',
    };
  }

  return out;
}

export function mapInventoryFromResponse(raw: HeroResponse['inventory']): EquipmentItem[] | undefined {
  if (!raw?.length) return undefined;
  return raw.map((it) => ({
    id: it.id,
    slot: it.slot,
    formId: it.formId ?? '',
    name: it.name,
    rarity: it.rarity as Rarity,
    ilvl: it.ilvl ?? 1,
    primaryStat: it.primaryStat ?? 0,
    statType: it.statType ?? 'mixed',
  }));
}

/** REST NPC response is a patch for excursion fields; only absent keys retain live values. */
export function heroPatchResponseToState(
  res: HeroResponse,
  current: HeroState | null | undefined,
): HeroState {
  const incoming = heroResponseToState(res);
  if (!current) return incoming;
  const has = (key: keyof HeroResponse) => Object.prototype.hasOwnProperty.call(res, key);
  return {
    ...incoming,
    currentTownId: has('currentTownId') ? incoming.currentTownId : current.currentTownId,
    currentActivity: has('currentActivity') ? incoming.currentActivity : current.currentActivity,
    activeCrierSpeech: has('activeCrierSpeech') ? incoming.activeCrierSpeech : current.activeCrierSpeech,
    excursionKind: has('excursionKind') ? incoming.excursionKind : current.excursionKind,
    excursionPhase: has('excursionPhase') ? incoming.excursionPhase : current.excursionPhase,
    townTourPhase: has('townTourPhase') ? incoming.townTourPhase : current.townTourPhase,
    townTourNpcId: has('townTourNpcId') ? incoming.townTourNpcId : current.townTourNpcId,
    townTourExitPending: has('townTourExitPending') ? incoming.townTourExitPending : current.townTourExitPending,
  };
}

export function heroResponseToState(res: HeroResponse): HeroState {
  const now = Date.now();
  return {
    id: res.id,
    name: res.name,
    modelVariant: normalizeHeroModelVariant(res.modelVariant),
    hp: res.hp,
    maxHp: res.maxHp,
    position: { x: res.positionX ?? 0, y: res.positionY ?? 0 },
    currentTownId: res.currentTownId,
    serverActivityState: res.state,
    currentActivity: parseHeroActivity(res.currentActivity),
    activeCrierSpeech: parseActiveCrierSpeech(res.activeCrierSpeech),
    restKind: res.restKind,
    excursionPhase: res.excursionPhase,
    excursionKind:
      res.excursionKind === 'roadside' ||
      res.excursionKind === 'adventure' ||
      res.excursionKind === 'town' ||
      res.excursionKind === 'road_poi' ||
      res.excursionKind === 'hero_meet' ||
      res.excursionKind === 'wandering_merchant'
        ? (res.excursionKind as HeroState['excursionKind'])
        : undefined,
    townTourPhase: res.townTourPhase,
    townTourNpcId: res.townTourNpcId,
    townTourExitPending: res.townTourExitPending ?? false,
    attackSpeed: res.attackSpeed ?? res.speed,
    damage: res.attackPower ?? res.attack,
    defense: res.defensePower ?? res.defense,
    weaponType: (res.gear?.main_hand?.subtype ?? res.weapon?.type ?? 'sword') as HeroState['weaponType'],
    weaponName: res.gear?.main_hand?.name ?? res.weapon?.name ?? '',
    weaponRarity: (res.gear?.main_hand?.rarity ?? res.weapon?.rarity ?? 'common') as Rarity,
    weaponIlvl: res.gear?.main_hand?.ilvl ?? res.weapon?.ilvl,
    armorType: (res.gear?.chest?.subtype ?? res.armor?.type ?? 'medium') as HeroState['armorType'],
    armorName: res.gear?.chest?.name ?? res.armor?.name ?? '',
    armorRarity: (res.gear?.chest?.rarity ?? res.armor?.rarity ?? 'common') as Rarity,
    armorIlvl: res.gear?.chest?.ilvl ?? res.armor?.ilvl,
    activeBuffs: mapHeroBuffsFromServer(res.buffs, now),
    debuffs: mapHeroDebuffsFromServer(res.debuffs, now),
    level: res.level,
    xp: res.xp,
    xpToNext: res.xpToNext ?? xpToNextLevel(res.level || 1),
    gold: res.gold,
    strength: res.strength,
    constitution: res.constitution,
    agility: res.agility,
    luck: res.luck,
    reviveCount: res.reviveCount,
    subscriptionActive: res.subscriptionActive,
    manualRevivesUnlimited: typeof res.manualRevivesUnlimited === 'boolean' ? res.manualRevivesUnlimited : undefined,
    buffFreeChargesRemaining: res.buffFreeChargesRemaining,
    buffQuotaPeriodEnd: res.buffQuotaPeriodEnd,
    buffCharges: mapBuffCharges(res.buffCharges),
    potions: res.potions ?? 0,
    moveSpeed: res.moveSpeed,
    equipment: mapEquipment(res.equipment, res),
    inventory: mapInventoryFromResponse(res.inventory),
    equipmentRevision: res.equipmentState?.revision,
    debuffCatalogDurations: debuffDurationsFromCatalog(res.debuffCatalog),
  };
}
